import type { JSX } from "react";
import { useMemo, useState } from "react";

import { LockOutlined } from "@ant-design/icons";
import HCaptcha from "@hcaptcha/react-hcaptcha";
import { zodResolver } from "@hookform/resolvers/zod";
import {
    Button,
    CardFooter,
    FormField,
    FormSubtitle,
    FormTitle,
} from "@impero-com/ui";
import { Input } from "antd";
import { Controller, useForm } from "react-hook-form";
import { Link } from "react-router-dom";
import { z } from "zod";

import { ENVIRONMENT } from "../../../env";
import { SwitchToTwoFactor } from "../../../types/switchToTwoFactor";
import { isUnauthenticated } from "../../../utils/auth";
import { t } from "../../../utils/i18n";
import { assertNever } from "../../../utils/obj";
import { notifyRequestError } from "../../../utils/query_client";
import { loginRedirectToRoot } from "../../../utils/url";
import FormCard from "../../formCard";
import FormCardContainer from "../../formCardContainer";

import { LoginRequest, useLoginMutation } from "./queries/useLoginMutation";
import * as theme from "./theme.css";

interface PasswordFormValues {
    password: string;
    captcha?: string;
}

interface PasswordFormProps {
    rightSide: JSX.Element;
    title: string;
    email: string;
    hasSSO: boolean;
    hasForgotPassword: boolean;
    login: LoginRequest;
    requiresHumanVerification: boolean;
    onSwitchToSetupTwoFactor: (challenge: string) => void;
    onSwitchToTwoFactor: (params: SwitchToTwoFactor) => void;
    onSwitchToSSO: () => void;
    onSwitchToEmailForm: () => void;
}

function PasswordForm({
    rightSide,
    title,
    email,
    hasSSO,
    hasForgotPassword,
    login,
    requiresHumanVerification: initialRequiresHumanVerification,
    onSwitchToSetupTwoFactor,
    onSwitchToTwoFactor,
    onSwitchToSSO,
    onSwitchToEmailForm,
}: PasswordFormProps) {
    const [requiresHumanVerification, setRequiresHumanVerification] = useState(
        initialRequiresHumanVerification
    );

    const schema = useMemo(() => {
        const base = z.object({
            password: z.string().min(1, t("Login_PasswordRequired")),
            captcha: z.string().optional(),
        });
        return requiresHumanVerification
            ? base.refine((values) => !!values.captcha, { path: ["captcha"] })
            : base;
    }, [requiresHumanVerification]);

    const { control, handleSubmit, setValue, setError } =
        useForm<PasswordFormValues>({
            resolver: zodResolver(schema),
            defaultValues: { password: "", captcha: "" },
        });

    const loginMutation = useLoginMutation(login);

    const submit = handleSubmit(({ password, captcha }) => {
        loginMutation.mutate(
            { email, password, captcha },
            {
                onSuccess: () => {
                    void loginRedirectToRoot();
                },
                onError: (error) => {
                    if (!error.response) {
                        notifyRequestError(error);
                        return;
                    }
                    const { status, data } = error.response;
                    switch (status) {
                        case 401: {
                            if (!isUnauthenticated(data)) {
                                throw new Error(t("Login_UnknownAuthType"));
                            }
                            switch (data.status) {
                                case "notAuthenticated":
                                    setValue("password", "");
                                    setError("password", {
                                        message: t(
                                            "Login_UsernameOrPasswordIncorrect"
                                        ),
                                    });
                                    if (data.requiresHumanVerification) {
                                        setRequiresHumanVerification(true);
                                    }
                                    break;
                                case "setupRequired":
                                    onSwitchToSetupTwoFactor(
                                        data.challenge ?? ""
                                    );
                                    break;
                                case "pinRequired":
                                    onSwitchToTwoFactor({
                                        type: "DEFAULT",
                                        challenge: data.challenge ?? "",
                                    });
                                    break;
                                case "phoneNotVerified":
                                    // Only PIN validation can end here, not a password login.
                                    throw new Error(
                                        t("Login_UnknownAuthType") +
                                            ": " +
                                            data.status
                                    );
                                default:
                                    return assertNever(data);
                            }
                            break;
                        }
                        case 403:
                            setValue("password", "");
                            setError("password", {
                                message: t("Login_UnknownUser"),
                            });
                            break;
                        default:
                            notifyRequestError(error);
                    }
                },
            }
        );
    });

    return (
        <FormCardContainer rightSide={rightSide}>
            <FormTitle title={title} />
            <FormSubtitle
                subtitle={t("Login_PasswordFormSubtitle", { email })}
            />
            <FormCard onSubmitForm={(e) => void submit(e)}>
                <Controller
                    control={control}
                    name="password"
                    render={({ field, fieldState }) => (
                        <FormField error={fieldState.error?.message}>
                            <Input
                                {...field}
                                prefix={
                                    <LockOutlined
                                        className={theme.iconOpacity}
                                    />
                                }
                                type="password"
                                placeholder={t("Login_Password")}
                                autoFocus
                            />
                        </FormField>
                    )}
                />
                {requiresHumanVerification ? (
                    <Controller
                        control={control}
                        name="captcha"
                        render={({ field, fieldState }) => (
                            <FormField
                                className={theme.hcaptchaContainer}
                                error={fieldState.error?.message}
                            >
                                <HCaptcha
                                    size="normal"
                                    sitekey={
                                        ENVIRONMENT.IMPERO_HCAPTCHA_SITE_KEY
                                    }
                                    onVerify={(token) => field.onChange(token)}
                                />
                            </FormField>
                        )}
                    />
                ) : null}
                <div className={theme.formItem}>
                    <Button
                        id="login-button"
                        variant="primary"
                        type="submit"
                        loading={loginMutation.isPending}
                        block
                    >
                        {t("Login_LoginButton")}
                    </Button>
                </div>
                {hasSSO ? (
                    <div className={theme.ssoButton}>
                        <Button
                            id="switch-to-sso-button"
                            variant="link"
                            onClick={onSwitchToSSO}
                        >
                            {t("Login_UseDomainLogin")}
                        </Button>
                    </div>
                ) : null}
            </FormCard>

            {hasForgotPassword ? (
                <CardFooter>
                    <Link
                        to={{
                            pathname: "/forgot-password",
                            search: `?email=${encodeURIComponent(email)}`,
                        }}
                    >
                        {t("Login_ForgotPassword")}
                    </Link>
                    <a onClick={onSwitchToEmailForm}>
                        {t("Login_UseDifferentEmail")}
                    </a>
                </CardFooter>
            ) : (
                <CardFooter>
                    <a onClick={onSwitchToEmailForm}>
                        {t("Login_UseDifferentEmail")}
                    </a>
                </CardFooter>
            )}
        </FormCardContainer>
    );
}

export { PasswordForm };
